import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import bannerImg from "../assets/photos/banner.jpg";
import introVideo from "../assets/videos/intro.MP4";
import InstagramButton from "./InstagramButton";
import Reviews from "./Reviews";
import WhatsAppButton from "./WhatsAppButton";
import IPhoneMockup from "./IPhoneMockup";

// Animation variants for consistent animations
const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 },
};

const Home = () => {
  const [showVideo, setShowVideo] = useState(false);
  
  // Auto-play demo video after 3.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVideo(true);
    }, 3500);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      <Box
        id="home"
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, #1f6cb9 30%, #024f8e 100%)",
            animation: "gradientBreathing 8s ease infinite",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\",%3E%3Cpath d=\"M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\" fill=\"rgba(255,255,255,.03)\" fill-rule=\"evenodd\"/%3E%3C/svg%3E')",
            opacity: 0.4,
          },
          "@keyframes gradientBreathing": {
            "0%, 100%": {
              opacity: 1,
              background: "linear-gradient(135deg, #1f6cb9 30%, #024f8e 100%)",
            },
            "50%": {
              opacity: 0.9,
              background: "linear-gradient(135deg, #1a5ea3 30%, #023e71 100%)",
            },
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: "100%",
            position: "relative",
            zIndex: 2,
            pt: { xs: "100px", sm: "120px", md: "100px" },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="center"
            sx={{
              minHeight: {
                xs: "calc(100vh - 120px)",
                sm: "calc(100vh - 140px)",
                md: "calc(100vh - 160px)",
              },
            }}
          >
            {/* Left Content Section */}
            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeInLeft}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.8 }}
              >
                {/* Main Title with Elegant Styling */}
                <Box
                  sx={{
                    position: "relative",
                    mb: 4,
                    pl: 3,
                    borderLeft: "4px solid #3cdf61",
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: {
                        xs: "2rem",
                        sm: "2.4rem",
                        md: "2.8rem",
                      },
                      lineHeight: 1.2,
                      letterSpacing: "-0.5px",
                      mb: 1,
                    }}
                  >
                    Prof(Dr)Taranjit Kaur's
                  </Typography>
                  
                  <Typography
                    variant="h2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 500,
                      fontSize: {
                        xs: "1.6rem",
                        sm: "1.8rem",
                        md: "2.2rem",
                      },
                      lineHeight: 1.2,
                      mb: 1,
                    }}
                  >
                    Healing Touch Centre
                  </Typography>
                  
                  <Typography
                    variant="h3"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontWeight: 400,
                      fontSize: {
                        xs: "1.2rem",
                        sm: "1.4rem",
                        md: "1.6rem",
                      },
                      lineHeight: 1.3,
                      fontStyle: "italic",
                    }}
                  >
                    Oral Maxillofacial Surgery • TMD • Dental Implant
                  </Typography>
                </Box>
                
                {/* Description with Clean Styling */}
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "12px",
                    p: { xs: 2.5, sm: 3 },
                    mb: 4,
                    maxWidth: "90%",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#f5f5f5",
                      fontSize: {
                        xs: "1rem",
                        sm: "1.1rem",
                        md: "1.2rem",
                      },
                      lineHeight: 1.7,
                      fontWeight: 300,
                    }}
                  >
                    With <Box component="span" sx={{ fontWeight: 600, color: "#3cdf61" }}>25 years</Box> of distinguished experience in Oral & Maxillofacial Surgery,
                    Dr. Taranjit Kaur leads our specialized center offering advanced surgical
                    and therapeutic solutions. Renowned for expertise in TMJ disorders,
                    dental implantology, and complex maxillofacial procedures.
                  </Typography>
                </Box>
              </motion.div>
              <Box
                sx={{
                  mt: 4,
                  mb: 2,
                  position: "relative",
                  width: "100%",
                  maxWidth: "90%",
                  mx: "auto",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <Box
                    sx={{
                      background: "rgba(18, 93, 145, 0.3)",
                      borderRadius: "8px",
                      p: { xs: 2.5, sm: 3 },
                      width: { xs: "100%", sm: "95%", md: 700 },
                      maxWidth: 700,
                      mx: "auto",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <motion.div
                      animate={{
                        opacity: [0.9, 1, 0.9],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                          width: "100%",
                          fontSize: {
                            xs: "1.1rem",
                            sm: "1.2rem",
                            md: "1.4rem",
                          },
                          textAlign: "center",
                          letterSpacing: "0.5px",
                          lineHeight: 1.3,
                          "& span": {
                            color: "#3cdf61",
                            fontWeight: 600,
                          },
                        }}
                      >
                        "Delivering <span>Personalized Care</span> with <span>Professionalism</span>"
                      </Typography>
                    </motion.div>
                  </Box>
                </motion.div>
              </Box>
            </Grid>

            {/* Right Image/Video Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                position: "relative",
                mt: { xs: 4, md: 3 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: { xs: "auto", md: "100%" },
                minHeight: { xs: "500px", md: "600px" },
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <AnimatePresence mode="wait">
                  {!showVideo ? (
                    <motion.div
                      key="banner-image"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.8 }}
                      style={{
                        position: "absolute",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <motion.div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          transformOrigin: "center center",
                        }}
                      >
                        <Box
                          component="img"
                          src={bannerImg}
                          alt="Healing Touch Centre"
                          sx={{
                            width: "80%",
                            display: "block",
                            margin: "0 auto",
                            maxWidth: "480px",
                            height: "auto",
                            borderRadius: "30px",
                            boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
                            transform: "perspective(1000px) rotateY(-10deg)",
                            transition: "all 0.5s ease-in-out",
                            "&:hover": {
                              transform:
                                "perspective(1000px) rotateY(0deg) scale(1.02)",
                              boxShadow: "0 40px 70px rgba(0,0,0,0.4)",
                            },
                          }}
                        />
                      </motion.div>

                      {/* Decorative Elements for Banner */}
                      <Box
                        sx={{
                          position: "absolute",
                          width: "200%",
                          height: "200%",
                          top: "-50%",
                          left: "-50%",
                          background:
                            "radial-gradient(circle, rgba(66,165,245,0.15) 0%, rgba(25,118,210,0) 70%)",
                          animation:
                            "pulseGradient 4s ease-in-out infinite alternate",
                          zIndex: -1,
                          "@keyframes pulseGradient": {
                            "0%": { transform: "scale(0.8)", opacity: 0.5 },
                            "100%": { transform: "scale(1.2)", opacity: 0.8 },
                          },
                        }}
                      />

                      {/* Top Right Glow */}
                      <Box
                        sx={{
                          position: "absolute",
                          width: "200px",
                          height: "200px",
                          borderRadius: "50%",
                          background:
                            "radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)",
                          top: "-50px",
                          right: "-50px",
                          filter: "blur(20px)",
                          zIndex: 0,
                        }}
                      />

                      {/* Bottom Left Glow */}
                      <Box
                        sx={{
                          position: "absolute",
                          width: "150px",
                          height: "150px",
                          borderRadius: "50%",
                          background:
                            "radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)",
                          bottom: "-30px",
                          left: "-30px",
                          filter: "blur(15px)",
                          zIndex: 0,
                        }}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="iphone-mockup"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.8 }}
                      style={{
                        position: "absolute",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IPhoneMockup videoSrc={introVideo} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              mt: 1.3,
              mb: 2,
              gap: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "white",
                color: "primary.main",
                px: 5,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: "30px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                minWidth: "200px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                },
                transition: "all 0.3s ease-in-out",
              }}
              onClick={() => {
                document
                  .getElementById("contact")
                  .scrollIntoView({ behavior: "smooth" });
              }}
            >
              Book an Appointment
            </Button>
          </Box>
        </Container>
      </Box>

      <Reviews />
      <WhatsAppButton />
      <InstagramButton />
    </>
  );
};

export default Home;
